import { create } from "zustand";
import type { ActivityActor } from "src/lib/activityService";
import {
  getFaqConfig,
  normalizeFaqConfig,
  saveFaqConfig,
} from "src/lib/faqService";
import type { FaqConfig, FaqContentItem, FaqSection } from "src/types/faq";
import {
  faqEqual,
  moveArrayItem,
  newSectionId,
} from "src/components/faq/faqUtils";

export type FaqStore = {
  canManage: boolean;
  activityActor: ActivityActor | null;
  form: FaqConfig | null;
  savedBaseline: FaqConfig | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  success: string | null;
  expandedSection: number | null;

  setCanManage: (can: boolean) => void;
  setActivityActor: (actor: ActivityActor | null) => void;
  load: () => Promise<void>;
  save: () => Promise<void>;
  patchSection: (idx: number, patch: Partial<FaqSection>) => void;
  patchContentItem: (
    sectionIdx: number,
    itemIdx: number,
    patch: Partial<FaqContentItem>,
  ) => void;
  addSection: () => void;
  removeSection: (idx: number) => void;
  moveSectionByIndex: (from: number, to: number) => void;
  addContentRow: (sectionIdx: number) => void;
  removeContentRow: (sectionIdx: number, itemIdx: number) => void;
  moveRowByIndex: (sectionIdx: number, from: number, to: number) => void;
  toggleSection: (sectionIndex: number) => void;
  clearSuccess: () => void;
  reset: () => void;
};

const initialSnapshot = (): Pick<
  FaqStore,
  | "form"
  | "savedBaseline"
  | "loading"
  | "saving"
  | "error"
  | "success"
  | "expandedSection"
> => ({
  form: null,
  savedBaseline: null,
  loading: true,
  saving: false,
  error: null,
  success: null,
  expandedSection: null,
});

export const useFaqStore = create<FaqStore>((set, get) => ({
  canManage: false,
  activityActor: null,
  ...initialSnapshot(),

  setCanManage: (can) => set({ canManage: can }),

  setActivityActor: (actor) => set({ activityActor: actor }),

  load: async () => {
    set({ loading: true, error: null });
    try {
      const data = await getFaqConfig();
      const normalized = normalizeFaqConfig(data);
      set({
        form: normalized,
        savedBaseline: JSON.parse(JSON.stringify(normalized)) as FaqConfig,
        loading: false,
      });
    } catch (e: unknown) {
      set({
        error: e instanceof Error ? e.message : "Failed to load FAQ",
        loading: false,
      });
    }
  },

  save: async () => {
    const { canManage, form, savedBaseline, activityActor } = get();
    if (!canManage || !form || !savedBaseline || faqEqual(form, savedBaseline))
      return;
    set({ saving: true, error: null });
    try {
      await saveFaqConfig(form, activityActor ?? undefined);
      const next = normalizeFaqConfig(form);
      set({
        form: next,
        savedBaseline: JSON.parse(JSON.stringify(next)) as FaqConfig,
        success:
          "FAQ Saved! The changes will be visible in the FAQ section of the app.",
        saving: false,
      });
    } catch (e: unknown) {
      set({
        error: e instanceof Error ? e.message : "Failed to save",
        saving: false,
      });
    }
  },

  patchSection: (idx, patch) => {
    if (!get().canManage) return;
    set((s) => {
      if (!s.form) return s;
      const faq = [...s.form.faq];
      faq[idx] = { ...faq[idx], ...patch };
      return { form: { ...s.form, faq } };
    });
  },

  patchContentItem: (sectionIdx, itemIdx, patch) => {
    if (!get().canManage) return;
    set((s) => {
      if (!s.form) return s;
      const faq = [...s.form.faq];
      const content = [...faq[sectionIdx].content];
      content[itemIdx] = { ...content[itemIdx], ...patch };
      faq[sectionIdx] = { ...faq[sectionIdx], content };
      return { form: { ...s.form, faq } };
    });
  },

  addSection: () => {
    if (!get().canManage) return;
    set((s) => {
      if (!s.form) return s;
      const newIndex = s.form.faq.length;
      const section: FaqSection = {
        id: newSectionId(),
        title: "New category",
        content: [{ subTitle: "", description: "" }],
      };
      return {
        form: { ...s.form, faq: [...s.form.faq, section] },
        expandedSection: newIndex,
      };
    });
  },

  removeSection: (idx) => {
    if (!get().canManage) return;
    set((s) => {
      if (!s.form) return s;
      const faq = s.form.faq.filter((_, i) => i !== idx);
      let expandedSection = s.expandedSection;
      if (expandedSection !== null) {
        if (expandedSection === idx) expandedSection = null;
        else if (expandedSection > idx) expandedSection -= 1;
      }
      return { form: { ...s.form, faq }, expandedSection };
    });
  },

  moveSectionByIndex: (from, to) => {
    if (!get().canManage || from === to) return;
    set((s) => {
      if (!s.form) return s;
      const faq = moveArrayItem(s.form.faq, from, to);
      let expandedSection = s.expandedSection;
      if (expandedSection !== null) {
        if (expandedSection === from) expandedSection = to;
        else if (from < to && expandedSection > from && expandedSection <= to)
          expandedSection -= 1;
        else if (from > to && expandedSection < from && expandedSection >= to)
          expandedSection += 1;
      }
      return { form: { ...s.form, faq }, expandedSection };
    });
  },

  addContentRow: (sectionIdx) => {
    if (!get().canManage) return;
    set((s) => {
      if (!s.form) return s;
      const faq = [...s.form.faq];
      const content = [
        ...faq[sectionIdx].content,
        { subTitle: "", description: "" },
      ];
      faq[sectionIdx] = { ...faq[sectionIdx], content };
      return { form: { ...s.form, faq } };
    });
  },

  removeContentRow: (sectionIdx, itemIdx) => {
    if (!get().canManage) return;
    set((s) => {
      if (!s.form) return s;
      const faq = [...s.form.faq];
      const content = faq[sectionIdx].content.filter((_, i) => i !== itemIdx);
      faq[sectionIdx] = { ...faq[sectionIdx], content };
      return { form: { ...s.form, faq } };
    });
  },

  moveRowByIndex: (sectionIdx, from, to) => {
    if (!get().canManage || from === to) return;
    set((s) => {
      if (!s.form) return s;
      const faq = [...s.form.faq];
      const content = moveArrayItem([...faq[sectionIdx].content], from, to);
      faq[sectionIdx] = { ...faq[sectionIdx], content };
      return { form: { ...s.form, faq } };
    });
  },

  toggleSection: (sectionIndex) =>
    set((s) => ({
      expandedSection: s.expandedSection === sectionIndex ? null : sectionIndex,
    })),

  clearSuccess: () => set({ success: null }),

  reset: () =>
    set({
      canManage: false,
      activityActor: null,
      ...initialSnapshot(),
    }),
}));
